import React, { useState, useRef } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  IconButton,
  Divider,
  MenuList,
  MenuItem,
  ListItemText,
  Icon,
  Icons,
  Typography,
  Popover,
} from '@nextiva/next-ui-components';
import { check, RULES_CONSTANTS } from '@nextiva/global-store';
import { getPresenceIndicatorType, isActiveFeature } from '../../utils';
import { newAnchorOrigin, newTransformOrigin, topLeftAnchorOrigin } from '../../constants/popoverConfig';
import {
  StyledMenu,
  StyledListItemText,
  StyledNarrowCheckIcon,
  StyledIcon,
  StyledInput,
  StyledForm,
  StyledSmallIcon,
  StyledLabel,
  StyledTypography,
  StyledBillingAndLicensingContainer,
  StyledNewIconContainer,
} from './AvatarMenu.styled';
import { PresenceIndicator } from './PresenceIndicator.styled';
import { flags, routes, PRESENCE_TYPE, PRESENCE_STATUS_LABEL, PRESENCE_STATUS } from '../../constants';
import { usePresence } from '../../hooks/usePresence';
import { useFeatureFlagState } from '../../hooks/useFeatureFlagState';
import { usePermission } from '../../hooks/usePermission';
import { capitalize, dndUntilShortDisplay } from '../../utils';
import { DNDSettingsPopover } from './DNDSettingsPopover';
import { format } from 'date-fns';
import { del } from 'idb-keyval';

const avatarId = 'global-header-avatar';
const presenceId = 'global-header-presence';
const dndId = 'global-header-dnd';
const {
  PLATFORM_ADMIN: { PLATFORM_ADMIN, SUPER_ADMIN, APP_ACCESS },
} = RULES_CONSTANTS;
const rules = [`${PLATFORM_ADMIN}.${SUPER_ADMIN}`, `${PLATFORM_ADMIN}.${APP_ACCESS}`];

const featureFlags = [
  flags.launchpadConnect,
  flags.userSettingsDisplayNewSPA,
  flags.moonshotOutage,
  flags.globalNavigationPresenceCheck,
  flags.connectPresenceCheck,
  flags.dndStatus,
  flags.purchaseLicenseFlow,
];
const corpAccounts = [3203838, 3676719, 3199278, 3202380, 2114809];

export const AvatarMenu = ({ children, globalStoreData, menuOptions = [], onSignOut, presenceStatus }) => {
  const featureFlagState = useFeatureFlagState(featureFlags);
  const { data: permissionData } = usePermission(globalStoreData.user?.accountInfo?.corpAcctNbr);
  const hasBaseConnect = isActiveFeature('BASE_NEXTIVA_CONNECT', globalStoreData.licenses);
  const hasConnect = featureFlagState[flags.launchpadConnect].isEnabled && hasBaseConnect;
  const isNextivaTenant = corpAccounts.includes(Number(globalStoreData?.user?.accountInfo?.corpAcctNbr));
  const showDNDStatus = featureFlagState[flags.dndStatus].isEnabled;
  const billingUrl = featureFlagState[flags.moonshotOutage].isEnabled
    ? routes.billingSummary
    : routes.billingPayment;
  const isPurchaseLicenseFlowEnabled = featureFlagState[flags.purchaseLicenseFlow].isEnabled;
  const currentDate = new Date();
  const [customDNDUntilDate, setCustomDNDUntilDate] = useState(currentDate);
  const [customDNDUntilTime, setCustomDNDUntilTime] = useState(format(new Date(), 'HH:mm'));

  const checkPermission = () => permissionData?.data.some((data) => data.grant);
  const getPresenceFlag = () => {
    if (window.location.pathname.includes('nextiva-connect') || window.location.hostname === 'localhost') {
      return featureFlagState[flags.connectPresenceCheck].isEnabled && hasConnect;
    }
    return featureFlagState[flags.globalNavigationPresenceCheck].isEnabled && hasConnect;
  };

  const presenceFlag = getPresenceFlag();

  const { presenceFromAPI, updatePresence } = usePresence();
  const [customMessage, setCustomMessage] = useState(presenceFromAPI?.message || '');
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isPresenceMenuOpen, setIsPresenceMenuOpen] = useState(false);
  const [isDNDSettingsOpen, setIsDNDSettingsOpen] = useState(false);
  const menuRef = useRef();
  const avatarRef = useRef();
  const dndRef = useRef();
  const isAutomatic = presenceFromAPI?.isAutomatic;
  const message = (
    presenceStatus ||
    PRESENCE_STATUS_LABEL[presenceFromAPI?.status] ||
    PRESENCE_STATUS_LABEL[PRESENCE_STATUS.AUTOMATIC]
  )
    .toString()
    .toLowerCase();
  const presenceIndicatorType = {
    presenceKey:
      message === 'donotdisturb'
        ? PRESENCE_TYPE.DO_NOT_DISTURB
        : !presenceStatus
        ? getPresenceIndicatorType(presenceFromAPI?.status)
        : presenceStatus,
    customMessage: presenceFromAPI?.message,
  };
  const hasAccess = rules.some((rule) => check(globalStoreData, rule));
  const dndMenuItemSubtitleLabel = (dndUntil) => `until ${dndUntil}`;
  const sensitivity = 0.2; //speed less than 0.2px/ms means "hovering over an element"
  let isOverPresenceIndicator = false;
  let leavePresenceMenu = false;
  let prevX = -1;
  let prevY = -1;
  let prevTime, lastTime, timerOpenPresenceMenu, timerClosePresenceMenu;

  const closeAvatarMenu = () => {
    setIsAvatarMenuOpen(false);
  };

  const onUpdatePresence = (status) => {
    setCustomDNDUntilDate(new Date());
    setCustomDNDUntilTime(new Date());
    updatePresence({ status, message: customMessage });
    closeMenus();
  };

  const onCustomMessageInputChange = (event) => {
    setCustomMessage(event.target.value);
  };

  const onCustomMessageInputSave = () => {
    updatePresence({ message: customMessage, status: presenceFromAPI.userSettingStatus });
  };

  const openPresenceMenu = () => {
    setCustomMessage(presenceFromAPI?.message || '');
    setIsPresenceMenuOpen(true);
  };

  const trackMouseSpeed = (event) => {
    let lastX = event.pageX;
    let lastY = event.pageY;
    let mouseSpeed;
    lastTime = Date.now();
    if (!lastTime || lastTime === prevTime) {
      mouseSpeed = 0;
    } else {
      const mouseDistance = Math.sqrt(Math.pow(prevX - lastX, 2) + Math.pow(prevY - lastY, 2));
      mouseSpeed = mouseDistance / (lastTime - prevTime);
    }

    if (mouseSpeed < sensitivity) {
      return;
    } else {
      if (isOverPresenceIndicator) {
        clearTimeout(timerOpenPresenceMenu);
        isOverPresenceIndicator = false;
      }
      prevX = lastX;
      prevY = lastY;
      prevTime = lastTime;
    }
  };

  const hoverPresenceIndicator = (event) => {
    if (isOverPresenceIndicator) {
      return;
    }
    isOverPresenceIndicator = true;
    prevX = event.pageX;
    prevY = event.pageY;
    prevTime = Date.now();
    timerOpenPresenceMenu = setTimeout(() => {
      openPresenceMenu();
      isOverPresenceIndicator = false;
    }, 250);
  };

  const leavePresenceIndicator = () => {
    if (isOverPresenceIndicator) {
      clearTimeout(timerOpenPresenceMenu);
      isOverPresenceIndicator = false;
    }
    if (isPresenceMenuOpen) {
      leavePresenceMenu = true;
      timerClosePresenceMenu = setTimeout(() => {
        setIsPresenceMenuOpen(false);
      }, 1000);
    }
  };

  const closeMenus = () => {
    setIsPresenceMenuOpen(false);
    setIsAvatarMenuOpen(false);
  };

  const enterPresenceMenu = () => {
    if (leavePresenceMenu) {
      clearTimeout(timerClosePresenceMenu);
      leavePresenceMenu = false;
    }
  };

  const closePresenceMenu = () => {
    leavePresenceMenu = true;
    setIsPresenceMenuOpen(false);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    onUpdatePresence(presenceFromAPI.userSettingStatus);
  };

  const handleOpenDNDSettings = () => {
    setIsDNDSettingsOpen(true);
    closeMenus();
  };

  const handleSignOut = () => {
    closeAvatarMenu();

    if (onSignOut) {
      onSignOut();
    }
  };

  const handleSwitchAccounts = async (event) => {
    event.preventDefault();
    /*
      Each IndexedDB store is scoped to a specific origin such as `nextiva.nextos.com` or `myfaketenant.nextos.com` and JS cannot access IDB stores across origins.
      So to comply with security's request that we delete the IndexedDB cache when a user signs out, we also need to delete it when the user switches tenants.
      This is because if a user switches from "Account A" to "Account B", if they click "sign out" during their session in Account B we can only 
      delete the IndexedDB store for Account B since we no longer have access to the database scoped to Account A (because changing tenants changes the origin).
    */
    try {
      const indexedDBKey = window.sessionStorage.getItem('indexedDBKey');
      if (indexedDBKey) await del(indexedDBKey);
    } catch (error) {
      console.error('error while clearing IndexedDB:', error);
    } finally {
      closeAvatarMenu();
      window.location = '/Platform/profile/select';
    }
  };

  return (
    <>
      <IconButton
        aria-label="open menu"
        id={`${avatarId}-button`}
        ref={avatarRef}
        size="small"
        onClick={() => setIsAvatarMenuOpen(true)}
        data-testid="GlobalHeader-AvatarMenu-AvatarMenuButton"
      >
        <Avatar presence={presenceFlag ? presenceIndicatorType : undefined} size={32}>
          {children}
        </Avatar>
      </IconButton>
      <StyledMenu
        open={isAvatarMenuOpen}
        anchorEl={avatarRef.current}
        onClose={closeAvatarMenu}
        anchorOrigin={newAnchorOrigin}
        transformOrigin={newTransformOrigin}
        PaperProps={{
          ref: menuRef,
          id: `${avatarId}-menu`,
        }}
      >
        {presenceFlag && (
          <MenuItem
            id={`${avatarId}-menu-item-presence`}
            onClick={openPresenceMenu}
            onMouseOver={hoverPresenceIndicator}
            onMouseMove={trackMouseSpeed}
            onMouseLeave={leavePresenceIndicator}
            data-testid={`GlobalHeader-AvatarMenu-${message}-MenuItem`}
          >
            {message !== 'donotdisturb' && message !== 'do not disturb' && (
              <PresenceIndicator
                type={presenceIndicatorType.presenceKey}
                data-testid="GlobalHeader-AvatarMenu-PresenceIndicator"
              />
            )}
            {(message === 'donotdisturb' || message === 'do not disturb') && (
              <Icon
                icon={Icons.dnd}
                sx={{ width: '8px', height: '8px', marginRight: '8px' }}
                htmlColor={'#DB3545'}
                data-testid="GlobalHeader-AvatarMenu-PresenceIndicator"
              />
            )}
            <StyledTypography title={message} variant="body2">
              {capitalize(message === 'donotdisturb' ? 'do not disturb' : message)}
            </StyledTypography>
            <Icon
              icon={Icons.caretRight}
              htmlColor="#758BA3"
              data-testid="GlobalHeader-AvatarMenu-CaretIcon"
            />
          </MenuItem>
        )}
        <MenuItem
          component="a"
          id={`${avatarId}-menu-item-settings`}
          href={
            featureFlagState[flags.userSettingsDisplayNewSPA].isEnabled
              ? '/apps/user-settings/#'
              : '/apps/home/#/user/MyProfile'
          }
          onClick={closeAvatarMenu}
          data-testid="GlobalHeader-AvatarMenu-MySettingsMenuItem"
        >
          <ListItemText>
            <Typography variant="body2">My settings</Typography>
          </ListItemText>
        </MenuItem>
        {hasAccess && (
          <>
            <MenuItem
              component="a"
              id={`${avatarId}-menu-item-administration`}
              href="/apps/platform-admin/#/users"
              onClick={closeAvatarMenu}
              data-testid="GlobalHeader-AvatarMenu-AdministrationMenuItem"
            >
              <ListItemText>
                <Typography variant="body2">Administration</Typography>
              </ListItemText>
            </MenuItem>
            {globalStoreData?.user?.accountInfo?.isDirectBilling && (
              <MenuItem
                component="a"
                id={`${avatarId}-menu-item-billing`}
                href={billingUrl}
                onClick={closeAvatarMenu}
                data-testid="GlobalHeader-AvatarMenu-BillingMenuItem"
              >
                <ListItemText>
                  {isPurchaseLicenseFlowEnabled ? (
                    <StyledBillingAndLicensingContainer>
                      <Typography variant="body2">Billing and Licensing</Typography>
                      <StyledNewIconContainer>
                        <Typography variant="caption">New</Typography>
                      </StyledNewIconContainer>
                    </StyledBillingAndLicensingContainer>
                  ) : (
                    <Typography variant="body2">Billing</Typography>
                  )}
                </ListItemText>
              </MenuItem>
            )}
          </>
        )}
        {menuOptions.map(({ href, label }) => (
          <MenuItem
            key={href}
            component="a"
            href={href}
            onClick={closeAvatarMenu}
            data-testid={`GlobalHeader-AvatarMenu-${label}-Label`}
          >
            <ListItemText>
              <Typography variant="body2">{label}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
        <Divider />
        {checkPermission() && isNextivaTenant && (
          <MenuItem
            component="a"
            href="/apps/admin-tools"
            onClick={closeAvatarMenu}
            data-testid="GlobalHeader-AvatarMenu-SystemAdministration"
          >
            <ListItemText>
              <Typography variant="body2">System administration</Typography>
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          id={`${avatarId}-menu-item-switch-account`}
          component="a"
          href="/Platform/profile/select"
          onClick={handleSwitchAccounts}
          data-testid="GlobalHeader-AvatarMenu-SwitchAccountsMenuItem"
        >
          <ListItemText>
            <Typography variant="body2">Switch accounts</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem
          id={`${avatarId}-menu-item-sign-out`}
          component="a"
          href="/Platform/logout"
          onClick={handleSignOut}
          data-testid="GlobalHeader-AvatarMenu-SignOutMenuItem"
        >
          <ListItemText>
            <Typography variant="body2">Sign out</Typography>
          </ListItemText>
        </MenuItem>
      </StyledMenu>
      <Popover
        open={isPresenceMenuOpen}
        anchorEl={menuRef.current}
        onClose={closeMenus}
        anchorOrigin={topLeftAnchorOrigin}
        transformOrigin={newTransformOrigin}
        PaperProps={{
          id: `${presenceId}-menu`,
        }}
      >
        <div onMouseEnter={enterPresenceMenu} onMouseLeave={closePresenceMenu}>
          <StyledForm onSubmit={onSubmit}>
            <StyledLabel htmlFor="presence-status">
              <Typography variant="caption" data-testid="GlobalHeader-AvatarMenu-SetYourStatusTitle">
                Set your status
              </Typography>
            </StyledLabel>
            <StyledInput
              id="presence-status"
              onChange={onCustomMessageInputChange}
              onBlur={onCustomMessageInputSave}
              placeholder="What is your status?"
              value={customMessage}
              data-testid="GlobalHeader-AvatarMenu-StatusTextField"
            />
          </StyledForm>
          <MenuList>
            <MenuItem
              id={`${presenceId}-menu-item-automatic`}
              onClick={() => onUpdatePresence(PRESENCE_STATUS.AUTOMATIC)}
              data-testid="GlobalHeader-AvatarMenu-AutomaticMenuItem"
            >
              <StyledListItemText>
                {isAutomatic ? (
                  <StyledNarrowCheckIcon icon={Icons.check} data-testid="GlobalHeader-AvatarMenu-CheckIcon" />
                ) : (
                  <StyledIcon
                    icon={Icons.refresh}
                    sx={{ width: '13px' }}
                    data-testid="GlobalHeader-AvatarMenu-RefreshIcon"
                  />
                )}
                <Typography variant="body2">Automatic</Typography>
              </StyledListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              id={`${presenceId}-menu-item-online`}
              onClick={() => onUpdatePresence(PRESENCE_STATUS.ONLINE)}
              data-testid={`GlobalHeader-AvatarMenu-${
                PRESENCE_STATUS_LABEL[PRESENCE_STATUS.ONLINE]
              }-MenuItem`}
            >
              <StyledListItemText>
                {message === PRESENCE_TYPE.ONLINE && !isAutomatic ? (
                  <StyledSmallIcon icon={Icons.check} data-testid="GlobalHeader-AvatarMenu-CheckIcon" />
                ) : (
                  <PresenceIndicator
                    type={PRESENCE_TYPE.ONLINE}
                    data-testid="GlobalHeader-AvatarMenu-OnlinePresenceIndicator"
                  />
                )}
                <Typography variant="body2">{PRESENCE_STATUS_LABEL[PRESENCE_STATUS.ONLINE]}</Typography>
              </StyledListItemText>
            </MenuItem>
            <MenuItem
              id={`${presenceId}-menu-item-busy`}
              onClick={() => onUpdatePresence(PRESENCE_STATUS.BUSY)}
              data-testid={`GlobalHeader-AvatarMenu-${PRESENCE_STATUS_LABEL[PRESENCE_STATUS.BUSY]}-MenuItem`}
            >
              <StyledListItemText>
                {message === PRESENCE_TYPE.BUSY && !isAutomatic ? (
                  <StyledSmallIcon icon={Icons.check} data-testid="GlobalHeader-AvatarMenu-CheckIcon" />
                ) : (
                  <PresenceIndicator
                    type={PRESENCE_TYPE.BUSY}
                    data-testid="GlobalHeader-AvatarMenu-BusyPresenceIndicator"
                  />
                )}
                <Typography variant="body2">{PRESENCE_STATUS_LABEL[PRESENCE_STATUS.BUSY]}</Typography>
              </StyledListItemText>
            </MenuItem>
            {showDNDStatus && (
              <MenuItem
                component="a"
                id={`${presenceId}-menu-item-dnd`}
                onClick={handleOpenDNDSettings}
                data-testid={`GlobalHeader-AvatarMenu-${
                  PRESENCE_STATUS_LABEL[PRESENCE_STATUS.DO_NOT_DISTURB]
                }-MenuItem`}
                PaperProps={{
                  ref: dndRef,
                  id: `${dndId}-settings`,
                }}
              >
                <StyledListItemText>
                  <Icon
                    icon={
                      message === 'donotdisturb' || message === 'do not disturb' ? Icons.check : Icons.dnd
                    }
                    htmlColor={presenceFromAPI?.status === PRESENCE_STATUS.DO_NOT_DISTURB && '#758BA3'}
                    sx={{ width: '10px', height: '10px' }}
                    data-testid="GlobalHeader-AvatarMenu-DNDPresenceIndicator"
                  />
                  <Typography variant="body2">
                    {PRESENCE_STATUS_LABEL[PRESENCE_STATUS.DO_NOT_DISTURB]}
                  </Typography>
                  <Box sx={{ marginLeft: 'auto' }}>
                    {(message === 'donotdisturb' || message === 'do not disturb') && (
                      <Typography variant="body2" color="#758BA3">
                        {dndMenuItemSubtitleLabel(dndUntilShortDisplay(presenceFromAPI?.expiresAtDateTime))}
                      </Typography>
                    )}
                  </Box>
                </StyledListItemText>
              </MenuItem>
            )}
            <MenuItem
              id={`${presenceId}-menu-item-away`}
              onClick={() => onUpdatePresence(PRESENCE_STATUS.AWAY)}
              data-testid={`GlobalHeader-AvatarMenu-${PRESENCE_STATUS_LABEL[PRESENCE_STATUS.AWAY]}-MenuItem`}
            >
              <StyledListItemText>
                {message === PRESENCE_TYPE.AWAY && !isAutomatic ? (
                  <StyledSmallIcon icon={Icons.check} data-testid="GlobalHeader-AvatarMenu-CheckIcon" />
                ) : (
                  <PresenceIndicator
                    type={PRESENCE_TYPE.AWAY}
                    data-testid="GlobalHeader-AvatarMenu-AwayPresenceIndicator"
                  />
                )}
                <Typography variant="body2">{PRESENCE_STATUS_LABEL[PRESENCE_STATUS.AWAY]}</Typography>
              </StyledListItemText>
            </MenuItem>
            <MenuItem
              id={`${presenceId}-menu-item-offline`}
              onClick={() => onUpdatePresence(PRESENCE_STATUS.OFFLINE)}
              data-testid={`GlobalHeader-AvatarMenu-${
                PRESENCE_STATUS_LABEL[PRESENCE_STATUS.OFFLINE]
              }-MenuItem`}
            >
              <StyledListItemText>
                {message === PRESENCE_TYPE.OFFLINE && !isAutomatic ? (
                  <StyledSmallIcon icon={Icons.check} data-testid="GlobalHeader-AvatarMenu-CheckIcon" />
                ) : (
                  <PresenceIndicator
                    type={PRESENCE_TYPE.OFFLINE}
                    data-testid="GlobalHeader-AvatarMenu-OfflinePresenceIndicator"
                  />
                )}
                <Typography variant="body2">{PRESENCE_STATUS_LABEL[PRESENCE_STATUS.OFFLINE]}</Typography>
              </StyledListItemText>
            </MenuItem>
          </MenuList>
        </div>
      </Popover>
      <Backdrop open={isDNDSettingsOpen} />
      <DNDSettingsPopover
        isDNDSettingsOpen={isDNDSettingsOpen}
        setIsDNDSettingsOpen={setIsDNDSettingsOpen}
        setIsPresenceMenuOpen={setIsPresenceMenuOpen}
        setIsAvatarMenuOpen={setIsAvatarMenuOpen}
        dndExpiresAtDateTime={presenceFromAPI?.expiresAtDateTime}
        updatePresence={(status) => updatePresence({ message: customMessage, ...status })}
        customDNDUntilDate={customDNDUntilDate}
        setCustomDNDUntilDate={setCustomDNDUntilDate}
        customDNDUntilTime={customDNDUntilTime}
        setCustomDNDUntilTime={setCustomDNDUntilTime}
      />
    </>
  );
};

AvatarMenu.displayName = 'AvatarMenu';
